import React from 'react';
import { List, Card, Row, Col, Tag, Button, Tooltip } from 'antd';
import styled from 'styled-components';
import { Ticket } from './type';
import { useSelector } from 'react-redux';
import { selectTranslations } from 'app/pages/App/slice/selectors';
import { LoadingOutlined } from '@ant-design/icons';
import Images from 'app/assets/img';
import useWindowSize from 'app/hooks/useWindowSize';
interface TicketListProps {
  tickets: Ticket[];
  loading: boolean;
  onSelect?: (
    ticket: Ticket,
    siteCode: string,
    siteName: string,
    productCode: string,
  ) => void;
  siteCode?: string;
  siteName?: string;
  productCode?: string;
}

const TicketCard = styled(Card)`
  margin-bottom: 16px;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  border: 1px solid #f0f0f0;

  .ant-card-body {
    padding: 16px;
  }
`;

const TicketImage = styled.img`
  width: 120px;
  height: 90px;
  object-fit: cover;
  border-radius: 8px;
`;

const TicketTitle = styled.div`
  font-weight: 700;
  font-size: 16px;
  color: #1f1f1f;
  margin-bottom: 4px;
`;

const TicketDescription = styled.div`
  font-size: 14px;
  color: #595959;
  margin-bottom: 8px;
`;

const PriceSection = styled.div<{ $isMobile?: boolean }>`
  text-align: ${({ $isMobile }) => ($isMobile ? 'left' : 'right')};
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: ${({ $isMobile }) => ($isMobile ? 'stretch' : 'flex-end')};
  ${({ $isMobile }) =>
    $isMobile &&
    `
      margin-top: 12px;
      gap: 8px;
    `}
`;
const SelectButton = styled(Button)<{ selected?: boolean }>`
  && {
    border-radius: 6px;
    height: 40px;
    width: 100px;
    font-weight: 600;
    color: ${props => (props.selected ? '#8c8c8c' : '#fff')};
    border: none;
    background: ${props =>
      props.selected
        ? '#f5f5f5'
        : 'linear-gradient(79.97deg, #f06f24 -7.79%, #f8b117 107.61%)'};
    display: flex;
    align-items: center;
    justify-content: center;

    &:hover,
    &:active,
    &:focus {
      filter: ${props => (props.selected ? 'none' : 'brightness(0.95)')};
      color: ${props => (props.selected ? '#8c8c8c' : '#fff')};
      background: ${props =>
        props.selected
          ? '#f5f5f5'
          : 'linear-gradient(79.97deg, #f06f24 -7.79%, #f8b117 107.61%)'};
    }

    @media (max-width: 768px) {
      width: 100%;
      /* max-width: 280px; */
    }
  }
`;

const PriceDiff = styled.div`
  font-size: 20px;
  font-weight: 700;
  color: #000;
`;

const StrikethroughPrice = styled.div`
  font-size: 12px;
  color: #8c8c8c;
  text-decoration: line-through;
`;
const FALLBACK_IMAGE = Images.ImgBanner;
export const TicketList: React.FC<TicketListProps> = ({
  tickets,
  loading,
  onSelect,
  siteCode,
  siteName,
  productCode,
}) => {
  const { device } = useWindowSize();
  const isMobile = device === 'mobile';
  // Selected visual state
  const [selectedId, setSelectedId] = React.useState<string | null>(null);
  const translations = useSelector(selectTranslations);
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('vi-VN', { style: 'decimal' }).format(value);
  };

  const handleTicketSelection = (item: Ticket) => {
    setSelectedId(item.id);

    // Call parent onSelect handler with all required params
    onSelect?.(item, siteCode || '', siteName || '', productCode || '');
  };

  // Sort tickets by full combo price ascending (prefer paxPrice when available)
  const sortedTickets = React.useMemo(() => {
    return (tickets || [])
      .slice()
      .sort((a, b) => (a.paxPrice ?? a.price) - (b.paxPrice ?? b.price));
  }, [tickets]);

  const minPrice =
    sortedTickets.length > 0
      ? sortedTickets[0].paxPrice ?? sortedTickets[0].price
      : 0;

  // Lazy load / load more
  const PAGE_SIZE = 10; // default show 10 items first
  const [visibleCount, setVisibleCount] = React.useState(PAGE_SIZE);

  // Reset visibleCount when tickets data changes
  React.useEffect(() => {
    setVisibleCount(Math.min(PAGE_SIZE, sortedTickets.length));
  }, [sortedTickets.length]);

  // Expanded descriptions state
  const [expandedIds, setExpandedIds] = React.useState<Record<string, boolean>>(
    {},
  );

  const showMore = () =>
    setVisibleCount(prev => Math.min(sortedTickets.length, prev + PAGE_SIZE));
  const hasMore = visibleCount < sortedTickets.length;
  const customIcon = <LoadingOutlined style={{ fontSize: 24 }} spin />;
  return (
    <>
      <List
        loading={{
          spinning: loading,
          indicator: customIcon,
        }}
        dataSource={sortedTickets.slice(0, visibleCount)}
        renderItem={item => {
          // Display full combo price per passenger (prefer paxPrice)
          const displayPrice = `${formatCurrency(
            item.paxPrice ?? item.price,
          )}đ`;

          const isExpanded = Boolean(expandedIds[item.id]);
          const handleImageError = (
            e: React.SyntheticEvent<HTMLImageElement>,
          ) => {
            e.currentTarget.onerror = null;
            e.currentTarget.src = FALLBACK_IMAGE;
          };
          return (
            <TicketCard bordered={false}>
              <Row gutter={24} wrap={isMobile}>
                <Col flex={isMobile ? '80px' : '130px'}>
                  <TicketImage
                    src={item.image}
                    alt={item.title}
                    onError={handleImageError}
                  />
                </Col>

                <Col flex="auto">
                  <Tooltip title={item.title} placement="topLeft">
                    <TicketTitle
                      style={{
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {item.title}
                    </TicketTitle>
                  </Tooltip>

                  <TicketDescription>
                    {/** Prefer combo_description from API, otherwise use description */}
                    {(() => {
                      const description =
                        item.combo_description || item.description || '';
                      const plain = description.replace(/<[^>]+>/g, '');
                      const showMoreNeeded = plain.length > 200;
                      const descHasHtml = /<[^>]+>/.test(description);

                      return (
                        <>
                          <div
                            style={{
                              display: '-webkit-box',
                              WebkitLineClamp: isExpanded ? 'none' : 3,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                            }}
                          >
                            {descHasHtml ? (
                              <div
                                dangerouslySetInnerHTML={{
                                  __html: description,
                                }}
                              />
                            ) : (
                              description
                            )}
                          </div>

                          {showMoreNeeded && (
                            <div style={{ marginTop: 8 }}>
                              <button
                                type="button"
                                onClick={() =>
                                  setExpandedIds(prev => ({
                                    ...prev,
                                    [item.id]: !prev[item.id],
                                  }))
                                }
                                style={{
                                  background: 'transparent',
                                  border: 'none',
                                  padding: 0,
                                  color: 'var(--color-9)',
                                  cursor: 'pointer',
                                }}
                              >
                                {isExpanded
                                  ? translations?.['b2b.txt.collapse'] ||
                                    'Thu gọn'
                                  : translations?.['b2b.txt.expand'] ||
                                    'Xem thêm'}
                              </button>
                            </div>
                          )}
                        </>
                      );
                    })()}
                  </TicketDescription>

                  <div style={{ marginTop: 8 }}>
                    {item.tags.map((tag, index) => (
                      <Tag
                        key={index}
                        style={{
                          background: '#f5f5f5',
                          border: '1px solid #d9d9d9',
                          color: '#595959',
                        }}
                      >
                        [{tag}]
                      </Tag>
                    ))}
                  </div>
                </Col>

                <Col
                  span={isMobile ? 24 : undefined}
                  flex={isMobile ? undefined : '200px'}
                >
                  <PriceSection $isMobile={isMobile}>
                    <div>
                      <PriceDiff>{displayPrice}</PriceDiff>
                    </div>
                    <SelectButton
                      selected={selectedId === item.id}
                      style={isMobile ? { width: '100%' } : undefined}
                      onClick={() => handleTicketSelection(item)}
                    >
                      {translations ? translations['b2b.txt.select'] : 'Chọn'}
                    </SelectButton>
                  </PriceSection>
                </Col>
              </Row>
            </TicketCard>
          );
        }}
      />

      {hasMore && (
        <div style={{ textAlign: 'center', marginTop: 12 }}>
          <Button onClick={showMore}>
            {translations?.['b2b.txt.viewMoreOptions'] ?? 'Xem lựa chọn khác'}
          </Button>
        </div>
      )}
    </>
  );
};
